import { motion } from "framer-motion";
import { ShieldCheck, Users, Warehouse, Monitor } from "lucide-react";
import { DecorativeLines } from "@/components/DecorativeLines";

const features = [
  {
    icon: ShieldCheck,
    title: "Certyfikowany serwis",
    description: "Posiadamy wszystkie niezbędne certyfikaty i uprawnienia do serwisowania urządzeń medycznych.",
  },
  {
    icon: Users,
    title: "Doświadczony zespół",
    description: "Nasz zespół składa się z wykwalifikowanych specjalistów z wieloletnim doświadczeniem.",
  },
  {
    icon: Warehouse,
    title: "Zaplecze logistyczno-magazynowe",
    description: "Dysponujemy własnym zapleczem logistyczno-magazynowym, zapewniającym sprawną obsługę operacyjną.",
  },
  {
    icon: Monitor,
    title: "Dedykowane rozwiązania IT",
    description: "Pracujemy w oparciu o własny, autorski system iReQuest wspierający procesy serwisowe i logistyczne.",
  },
];

export const About = () => {
  return (
    <section
      id="o-nas"
      className="section-spacing bg-[#F7F9F9] dark:bg-[#121418] relative"
      aria-label="O nas"
      data-testid="about-section"
    >
      <DecorativeLines variant="hero-shape" />
      <DecorativeLines variant="diagonal" />
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col gap-12">
          {/* Header and Text Content */}
          <motion.div
            initial={{ opacity: 0, y: -24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl"
          >
            <span className="text-[#EA8628] font-semibold text-sm uppercase tracking-wider">
              O nas
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-[#212738] dark:text-white">
              CKK ResQ — Profesjonalny
              <span className="text-[#EA8628]"> serwis sprzętu medycznego</span>
            </h2>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Text Content */}
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="space-y-5"
            >
              <p className="text-[#504D4D] dark:text-white/70 text-sm leading-relaxed text-justify">
                <strong className="text-[#EA8628] font-semibold">CKK ResQ</strong> to firma z wieloletnim doświadczeniem w serwisowaniu urządzeń medycznych, działająca nieprzerwanie <strong className="text-[#EA8628] font-semibold">od 2006 roku</strong>. Jesteśmy częścią <strong className="text-[#EA8628] font-semibold">Grupy Q</strong> - dynamicznie rozwijającej się grupy przedsiębiorstw, w skład której wchodzą wyspecjalizowane spółki <strong className="text-[#EA8628] font-semibold">CKK ResQ, ResQmed oraz IQmed.</strong>
              </p>
              <p className="text-[#504D4D] dark:text-white/70 text-sm leading-relaxed text-justify">
                Naszym priorytetem jest dbałość o klienta oraz najwyższe standardy profesjonalizmu w serwisie sprzętu medycznego. Wieloletnia praktyka oraz ekspercka wiedza naszych specjalistów pozwalają nam świadczyć usługi serwisowe na najwyższym poziomie dla placówek medycznych na terenie całego kraju.
              </p>
              <p className="text-[#504D4D] dark:text-white/70 text-sm leading-relaxed text-justify">
                Oprócz usług serwisowych <strong className="text-[#EA8628] font-semibold">świadczymy również kompleksowe usługi logistyczne dla naszych kontrahentów</strong>, obejmujące obsługę urządzeń, magazynowanie oraz wsparcie operacyjne. W <strong className="text-[#EA8628] font-semibold">Trzebini dysponujemy nowoczesnym zapleczem magazynowym</strong>, które umożliwia sprawną obsługę logistyczną sprzętu medycznego, w tym urządzeń zastępczych oraz części zamiennych.
              </p>
              <p className="text-[#504D4D] dark:text-white/70 text-sm leading-relaxed text-justify">
                Działamy w oparciu o <strong className="text-[#EA8628] font-semibold">trzy biura zlokalizowane w Milanówku, Trzebini oraz Łodzi</strong>, przy czym <strong className="text-[#EA8628] font-semibold">Milanówek i Trzebinia pełnią funkcję głównych siedzib serwisowych</strong>. Dzięki rozbudowanemu zapleczu technicznemu oraz sprawnej organizacji pracy przez nasz serwis <strong className="text-[#EA8628] font-semibold">rocznie przechodzi około 6 000 urządzeń medycznych</strong>, co potwierdza skalę działalności i zaufanie klientów.
              </p>
              <p className="text-[#504D4D] dark:text-white/70 text-sm leading-relaxed text-justify">
                Dodatkowo, jako część Grupy Q, <strong className="text-[#EA8628] font-semibold">jesteśmy producentem dedykowanych rozwiązań IT</strong>, wspierających procesy serwisowe, logistyczne oraz obsługę posprzedażową, co pozwala nam oferować partnerom kompleksowe i nowoczesne wsparcie technologiczne.
              </p>

              <div className="pt-4 flex flex-wrap gap-4">
                <a
                  href="https://resqmed.pl"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-[#504D4D] dark:text-white/80 hover:text-[#EA8628] dark:hover:text-white hover:border-[#EA8628]/50 transition-colors duration-200 text-sm"
                >
                  ResQmed →
                </a>
                <a
                  href="https://grupaq.pl"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-[#504D4D] dark:text-white/80 hover:text-[#EA8628] dark:hover:text-white hover:border-[#EA8628]/50 transition-colors duration-200 text-sm"
                >
                  GrupaQ →
                </a>
              </div>
            </motion.div>

            {/* Features - text over heart background */}
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex flex-col gap-10"
            >
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.1 * index }}
                  className="flex items-start gap-4"
                  data-testid={`about-feature-${index}`}
                >
                  <div className="w-11 h-11 rounded-full bg-[#EA8628]/15 dark:bg-[#EA8628]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <feature.icon className="w-5 h-5 text-[#EA8628]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#212738] dark:text-white leading-tight">
                      {feature.title}
                    </h3>
                    <p className="mt-1.5 text-sm text-[#504D4D] dark:text-white/65 leading-relaxed max-w-sm">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
